// PatientLayout.jsx
import { Col, Container, Modal, Row } from "react-bootstrap";
import { Outlet, useLocation, useParams } from "react-router-dom";
import TabsNav from "../../components/ui/TabsNav";
import Breadcrumbs from "../../components/ui/Breadcrumbs";
import userIcon from "../../media/icons-user.png";
import userTab from "../../media/icons8-user-tab.png";
import userPlan from "../../media/icons8-plan.png";
import userMoney from "../../media/icons8-money.png";
import userAnalyses from "../../media/icons8-laboratory.png";
import userDynamics from "../../media/icons8-dynamics.png";
import userHistory from "../../media/icons8-history.png";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getLocale } from "../../i18n/config";
import GetData from "../../services/getData";
import logPatientChange from "../../services/patientLogs";
import { buildPatientLogText } from "../../services/patientLogMessages";
import TitleWithIcon from "../../components/layout/pageTitle/PageTitle";
import EditableModal from "../../components/layout/editableModal/EditableModal";
// import PatientAnalysesAI from "./tabs/PatientAnalysesAI";
import PatientHistory from "./tabs/PatientHistory";
import PatientPayments from "./tabs/PatientPayments";
import PatientAiOrb from "./PatientAiOrb";
import "./PatientLayout.css";

const TAB_LABEL_KEYS = {
  contacts: "layout.tabs.contacts",
  profile: "layout.tabs.profile",
  analyses: "layout.tabs.analyses",
  inbody: "layout.tabs.inbody",
  dynamics: "layout.tabs.dynamics",
  diagnostics: "layout.tabs.diagnostics",
  documents: "layout.tabs.documents",
  plan: "layout.tabs.plan",
  "ai-analyses": "layout.tabs.ai-analyses",
  payments: "layout.tabs.payments",
  history: "layout.tabs.history",
  appointments: "layout.tabs.appointments",
  recommendations: "layout.tabs.recommendations",
};

const PROFILE_DOC_LABEL_KEYS = {
  primary: "layout.profileDocs.primary",
  repeat: "layout.profileDocs.repeat",
  anamnesis: "layout.profileDocs.anamnesis",
  "smart-diagnostics": "layout.profileDocs.smart-diagnostics",
};

const getTabLabel = (t, section) => {
  const key = TAB_LABEL_KEYS[section];
  return key ? t(key) : section;
};

const displayBriefValue = (value) => {
  if (value === 0) return "0";
  const text = String(value ?? "").trim();
  if (!text || text === "0000-00-00" || text === "0000-00-00 00:00:00") {
    return "—";
  }
  return text;
};

const GENDER_LABEL_KEYS = {
  "Мужской": "layout.gender.male",
  "Женский": "layout.gender.female",
};

const displayGender = (value, t) => {
  const key = GENDER_LABEL_KEYS[String(value ?? "").trim()];
  return key ? t(key) : displayBriefValue(value);
};

const formatBirthDate = (isoDate, locale, t) => {
  const raw = String(isoDate ?? "").trim();
  if (!raw || raw.startsWith("0000-00-00")) return "—";
  const [datePart] = raw.split(/[ T]/);
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return "—";

  const birth = new Date(Number(year), Number(month) - 1, Number(day));
  if (Number.isNaN(birth.getTime())) {
    return `${day}.${month}.${year}`;
  }

  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const monthDiff = now.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birth.getDate())) {
    age -= 1;
  }

  const formatted = birth.toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  return age >= 0 ? `${formatted} (${t("age", { count: age })})` : formatted;
};

const getDoctorName = (patient) => {
  const names = Array.isArray(patient?.doc_names?.value)
    ? patient.doc_names.value
        .map((docItem) =>
          typeof docItem === "string" ? docItem : docItem?.name,
        )
        .filter(Boolean)
    : [];
  if (names.length > 0) return displayBriefValue(names.join(", "));

  const doc = patient?.doc_name?.value;
  if (!doc) return "—";
  if (typeof doc === "string") return displayBriefValue(doc);
  return displayBriefValue(doc.name);
};

const getDoctorId = (patient) => {
  const doc = patient?.doc_name?.value;
  if (doc && typeof doc === "object" && doc.id) return String(doc.id);
  return "";
};

// Список id лечащих врачей (id_users приходит из patients_doctors; если связей
// нет — бэкенд отдаёт одиночный patients.id_user через doc_name).
const getDoctorIds = (patient) => {
  if (Array.isArray(patient?.id_users?.value)) {
    return patient.id_users.value
      .map((idItem) => String(idItem))
      .filter(Boolean);
  }
  const docId = getDoctorId(patient);
  return docId ? [docId] : [];
};

const normalizeBirthDateInput = (isoDate) => {
  const raw = String(isoDate ?? "").trim();
  if (!raw || raw.startsWith("0000-00-00")) return "";
  return raw.split(/[ T]/)[0];
};

const BRIEF_FIELD_LABEL_KEYS = {
  fio: "layout.fields.fio",
  phone: "layout.fields.phone",
  birthDate: "layout.fields.birthDate",
  gender: "layout.fields.gender",
  doc_ids: "layout.fields.doc_ids",
  id_clinic: "layout.fields.id_clinic",
  type: "layout.fields.type",
  mail: "layout.fields.mail",
  cart: "layout.fields.cart",
  comment: "layout.fields.comment",
};

const mapBriefFormData = (patient) => ({
  fio: patient?.fio?.value ?? "",
  phone: patient?.phone?.value ?? "",
  birthDate: normalizeBirthDateInput(patient?.birthDate?.value),
  gender: patient?.gender?.value ?? "",
  doc_id: getDoctorId(patient),
  doc_ids: getDoctorIds(patient),
  id_clinic: String(patient?.id_clinic?.value ?? ""),
  type: String(patient?.type?.value ?? ""),
  mail: patient?.mail?.value ?? "",
  cart: patient?.cart?.value ?? "",
  comment: patient?.comment?.value ?? "",
});

const normalizeComparableValue = (value) =>
  value == null ? "" : String(value).trim();

const collectChangedFields = (before, after, labelKeys, t) =>
  Object.keys(labelKeys).reduce((acc, key) => {
    const fromValue = normalizeComparableValue(before?.[key]);
    const toValue = normalizeComparableValue(after?.[key]);
    if (fromValue !== toValue) {
      acc.push({
        label: t(labelKeys[key]),
        from: before?.[key],
        to: after?.[key],
      });
    }
    return acc;
  }, []);

function getPlanIdFromPath(patientId, pathname) {
  const basePath = `/patient/${patientId}`;
  const relativePath = pathname.startsWith(basePath)
    ? pathname.slice(basePath.length).replace(/^\/+/, "")
    : "";
  const segments = relativePath ? relativePath.split("/").filter(Boolean) : [];

  if (segments[0] !== "plan" || !segments[1]) {
    return null;
  }

  return segments[1];
}

function buildPatientBreadcrumbs({
  patientId,
  patientName,
  planName,
  pathname,
  t,
}) {
  const basePath = `/patient/${patientId}`;
  const relativePath = pathname.startsWith(basePath)
    ? pathname.slice(basePath.length).replace(/^\/+/, "")
    : "";
  const segments = relativePath ? relativePath.split("/").filter(Boolean) : [];

  const items = [
    { label: t("layout.breadcrumbs.patients"), to: "/patient/" },
    {
      label: patientName || t("layout.breadcrumbs.patient"),
      to: `${basePath}/profile`,
    },
  ];

  if (!segments.length) {
    return items;
  }

  const [section, ...rest] = segments;
  const sectionLabel = getTabLabel(t, section);

  if (section === "plan") {
    items.push({
      label: sectionLabel,
      to: `${basePath}/plan`,
    });

    if (rest.length === 0) {
      return items;
    }

    const [planId, nestedSection] = rest;
    items.push({
      label: planName || t("layout.breadcrumbs.plan", { id: planId }),
      to: `${basePath}/plan/${planId}`,
    });

    if (nestedSection) {
      items.push({
        label: getTabLabel(t, nestedSection),
      });
    }

    return items;
  }

  if (section === "diagnostics") {
    items.push(
      rest.length
        ? { label: sectionLabel, to: `${basePath}/diagnostics` }
        : { label: sectionLabel },
    );
    if (rest[0] === "uzi") {
      items.push({
        label: t("layout.breadcrumbs.uziProtocol"),
      });
    }
    return items;
  }

  if (section === "profile") {
    const [docType] = rest;
    const docLabelKey = PROFILE_DOC_LABEL_KEYS[docType];

    if (docLabelKey) {
      items.push({
        label: sectionLabel,
        to: `${basePath}/profile`,
      });
      items.push({
        label: t(docLabelKey),
      });
      return items;
    }

    items.push({
      label: sectionLabel,
    });
    return items;
  }

  items.push({
    label: sectionLabel,
  });

  return items;
}

export default function PatientLayout() {
  const { t, i18n } = useTranslation("patient");
  const locale = getLocale(i18n.language);
  const { patientId } = useParams();
  const location = useLocation();
  const tabsStickyRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [patient, setPatient] = useState([]);
  const [planName, setPlanName] = useState("");
  const [showBriefEdit, setShowBriefEdit] = useState(false);
  const [showAiChat, setShowAiChat] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showPayments, setShowPayments] = useState(false);
  const [patientVersion, setPatientVersion] = useState(0);
  const [planGroupsSlotEl, setPlanGroupsSlotEl] = useState(null);

  const currentPlanId = useMemo(
    () => getPlanIdFromPath(patientId, location.pathname),
    [patientId, location.pathname],
  );

  useEffect(() => {
    getPatients(patientId);
  }, [patientId]);

  useEffect(() => {
    let cancelled = false;

    const loadPlanName = async () => {
      if (!patientId || !currentPlanId) {
        setPlanName("");
        return;
      }

      try {
        const data = await GetData({
          module: "patient",
          block: "treatment_plan_list",
          id_patient: patientId,
        });
        if (cancelled) return;

        const plans = data?.result || [];
        const matchedPlan = plans.find(
          (plan) => String(plan?.id) === String(currentPlanId),
        );
        setPlanName(matchedPlan?.name || "");
      } catch (err) {
        if (!cancelled) {
          setPlanName("");
        }
      }
    };

    loadPlanName();

    return () => {
      cancelled = true;
    };
  }, [patientId, currentPlanId]);

  const getPatients = async (id) => {
    try {
      const data = await GetData({
        module: "patient",
        block: "info",
        id_patient: id,
      });
      setPatient(data.result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (patient?.fio?.value) {
      document.title = patient.fio.value;
    }
  }, [patient]);

  useEffect(() => {
    if (typeof window === "undefined" || !tabsStickyRef.current) return;

    const root = document.documentElement;
    const updateTabsHeight = () => {
      const tabsHeight = Math.ceil(
        tabsStickyRef.current?.getBoundingClientRect().height || 0,
      );
      if (tabsHeight > 0) {
        root.style.setProperty(
          "--patient-tabs-sticky-height",
          `${tabsHeight}px`,
        );
      }
    };

    updateTabsHeight();

    let resizeObserver = null;
    if (window.ResizeObserver) {
      resizeObserver = new window.ResizeObserver(updateTabsHeight);
      resizeObserver.observe(tabsStickyRef.current);
    }
    window.addEventListener("resize", updateTabsHeight);

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("resize", updateTabsHeight);
    };
  }, []);

  const breadcrumbItems = useMemo(
    () =>
      buildPatientBreadcrumbs({
        patientId,
        patientName: patient?.fio?.value,
        planName,
        pathname: location.pathname,
        t,
      }),
    [patientId, patient?.fio?.value, planName, location.pathname, t],
  );

  const doctorOptions = useMemo(() => {
    const doctors = Array.isArray(patient?.all_doctors)
      ? patient.all_doctors
      : [];
    return doctors.map((doctor) => ({
      value: String(doctor?.id ?? ""),
      label: doctor?.name || "—",
    }));
  }, [patient?.all_doctors]);

  const clinicOptions = useMemo(() => {
    const clinics = Array.isArray(patient?.all_clinics)
      ? patient.all_clinics
      : [];
    return clinics.map((clinic) => ({
      value: String(clinic?.id ?? ""),
      label: clinic?.name || "—",
    }));
  }, [patient?.all_clinics]);

  const visitTypeOptions = useMemo(() => {
    const labels = patient?.visit_type_labels;
    if (labels && typeof labels === "object") {
      return Object.entries(labels).map(([value, label]) => ({
        value: String(value),
        label: String(label),
      }));
    }
    return [
      { value: "1", label: t("layout.visitTypes.clinic") },
      { value: "2", label: t("layout.visitTypes.online") },
    ];
  }, [patient?.visit_type_labels, t]);

  const briefEditFields = useMemo(
    () => [
      { name: "fio", label: t("layout.fields.fio"), type: "text", required: true },
      { name: "phone", label: t("layout.fields.phone"), type: "tel" },
      { name: "birthDate", label: t("layout.fields.birthDate"), type: "date" },
      {
        name: "gender",
        label: t("layout.fields.gender"),
        type: "select",
        options: [
          { value: "Мужской", label: t("layout.gender.male") },
          { value: "Женский", label: t("layout.gender.female") },
        ],
      },
      {
        name: "doc_ids",
        label: t("layout.fields.doc_ids"),
        type: "checkboxes",
        options: doctorOptions,
        hint: t("layout.doctorHint"),
      },
      {
        name: "id_clinic",
        label: t("layout.fields.id_clinic"),
        type: "select",
        options: clinicOptions,
      },
      {
        name: "type",
        label: t("layout.fields.type"),
        type: "select",
        options: visitTypeOptions,
      },
      { name: "mail", label: t("layout.fields.mail"), type: "email" },
      { name: "cart", label: t("layout.fields.cart"), type: "text" },
      {
        name: "comment",
        label: t("layout.fields.comment"),
        type: "textarea",
        rows: 4,
      },
    ],
    [doctorOptions, clinicOptions, visitTypeOptions, t],
  );

  const briefInitialData = useMemo(() => mapBriefFormData(patient), [patient]);

  const doctorNameById = (id) => {
    const match = doctorOptions.find(
      (option) => option.value === String(id ?? ""),
    );
    return match?.label || (id ? String(id) : "—");
  };

  const doctorNamesById = (ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return "—";
    return ids.map((id) => doctorNameById(id)).join(", ");
  };

  const clinicNameById = (id) => {
    const match = clinicOptions.find(
      (option) => option.value === String(id ?? ""),
    );
    return match?.label || (id ? String(id) : "—");
  };

  const visitTypeLabelById = (id) => {
    const match = visitTypeOptions.find(
      (option) => option.value === String(id ?? ""),
    );
    return match?.label || (id ? String(id) : "—");
  };

  const handleSaveBrief = async (formData) => {
    const previous = mapBriefFormData(patient);
    try {
      const result = await GetData({
        module: "patient",
        block: "info",
        id_patient: patientId,
        action: "change",
        fio: formData.fio,
        phone: formData.phone,
        birthDate: formData.birthDate,
        gender: formData.gender,
        mail: formData.mail,
        cart: formData.cart,
        comment: formData.comment ?? "",
        id_clinic: formData.id_clinic || "",
        type: formData.type || "",
        id_user:
          (Array.isArray(formData.doc_ids) ? formData.doc_ids[0] : "") || "",
        id_users: JSON.stringify(formData.doc_ids || []),
      });

      if (!result || result instanceof Error || !result.result) {
        throw new Error(result?.message || t("layout.saveFailed"));
      }

      const namedPrevious = {
        ...previous,
        doc_ids: doctorNamesById(previous.doc_ids),
        id_clinic: clinicNameById(previous.id_clinic),
        type: visitTypeLabelById(previous.type),
      };
      const namedNext = {
        ...formData,
        doc_ids: doctorNamesById(formData.doc_ids),
        id_clinic: clinicNameById(formData.id_clinic),
        type: visitTypeLabelById(formData.type),
      };

      await logPatientChange({
        idPatient: patientId,
        text: buildPatientLogText("patient_contact_updated", {
          changes: collectChangedFields(
            namedPrevious,
            namedNext,
            BRIEF_FIELD_LABEL_KEYS,
            t,
          ),
        }),
      });

      await getPatients(patientId);
      setPatientVersion((version) => version + 1);
      return true;
    } catch (err) {
      console.error("Ошибка сохранения данных пациента:", err);
      return false;
    }
  };

  if (!patient) {
    return <div>{t("notFoundPatient")}</div>;
  }

  const tabs = [
    {
      to: "profile",
      label: t("layout.tabs.profile"),
      icon: userTab,
    },
    // { to: "analyses", label: t("layout.tabs.analyses"), icon: userAnalyses },
    // { to: "inbody", label: t("layout.tabs.inbody"), icon: userDynamics },
    // { to: "dynamics", label: t("layout.tabs.dynamics"), icon: userDynamics },
    { to: "diagnostics", label: t("layout.tabs.diagnostics"), icon: userDynamics },
    { to: "plan", label: t("layout.tabs.plan"), icon: userPlan },
    { to: "documents", label: t("layout.tabs.documents"), icon: userTab },
  ];

  return (
    <Container fluid="md">
      <Row>
        <Col>
          <div className="patient-page">
            <TitleWithIcon
              icon={userIcon}
              title={patient?.fio?.value}
              showStatus={true}
              status={patient?.status?.value}
              warningTooltipText={patient?.comment?.value}
            />
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="page-content patient-page-layout">
            <div className="patient-main-column">
              <div className="tab-content block page-block">
                <div ref={tabsStickyRef} className="patient-tabs-sticky">
                  <TabsNav tabs={tabs} />
                </div>
                <Breadcrumbs items={breadcrumbItems} />
                <Outlet
                  context={{
                    refreshPatient: () => getPatients(patientId),
                    patientVersion,
                    planGroupsSlotEl,
                  }}
                />
              </div>
              <div
                ref={setPlanGroupsSlotEl}
                className="patient-plan-groups-slot"
              />
            </div>
            <div className="patient-sidebar">
              <div className="page-block patient-brief">
                <div className="patient-brief__fio">
                  {displayBriefValue(patient?.fio?.value)}
                </div>
                <div className="patient-brief__grid">
                  <div className="patient-brief__item">
                    <span className="patient-brief__label">
                      {t("layout.brief.idYc")}
                    </span>
                    <span className="patient-brief__value">
                      {displayBriefValue(patientId)} /{" "}
                      {displayBriefValue(patient?.id_yc?.value)}
                    </span>
                  </div>
                  <div className="patient-brief__item">
                    <span className="patient-brief__label">{t("layout.brief.phone")}</span>
                    <span className="patient-brief__value">
                      {displayBriefValue(patient?.phone?.value)}
                    </span>
                  </div>
                  <div className="patient-brief__item">
                    <span className="patient-brief__label">{t("layout.brief.birthDate")}</span>
                    <span className="patient-brief__value">
                      {formatBirthDate(patient?.birthDate?.value, locale, t)}
                    </span>
                  </div>
                  <div className="patient-brief__item">
                    <span className="patient-brief__label">{t("layout.brief.gender")}</span>
                    <span className="patient-brief__value">
                      {displayGender(patient?.gender?.value, t)}
                    </span>
                  </div>
                  <div className="patient-brief__item">
                    <span className="patient-brief__label">{t("layout.brief.doctor")}</span>
                    <span className="patient-brief__value">
                      {getDoctorName(patient)}
                    </span>
                  </div>
                  <div className="patient-brief__item">
                    <span className="patient-brief__label">{t("layout.brief.clinic")}</span>
                    <span className="patient-brief__value">
                      {displayBriefValue(patient?.clinic_name?.value)}
                    </span>
                  </div>
                  <div className="patient-brief__item">
                    <span className="patient-brief__label">{t("layout.brief.visitType")}</span>
                    <span className="patient-brief__value">
                      {displayBriefValue(patient?.type_label?.value)}
                    </span>
                  </div>
                  <div className="patient-brief__item">
                    <span className="patient-brief__label">{t("layout.brief.email")}</span>
                    <span className="patient-brief__value">
                      {displayBriefValue(patient?.mail?.value)}
                    </span>
                  </div>
                  <div className="patient-brief__item">
                    <span className="patient-brief__label">{t("layout.brief.cart")}</span>
                    <span className="patient-brief__value">
                      {displayBriefValue(patient?.cart?.value)}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  className="button-default patient-brief__edit"
                  onClick={() => setShowBriefEdit(true)}>
                  {t("layout.edit")}
                </button>
              </div>
              <button
                type="button"
                className="page-block patient-ai-entry"
                onClick={() => setShowAiChat(true)}>
                <span className="patient-ai-entry__visual" aria-hidden="true">
                  <PatientAiOrb />
                </span>
                <span className="patient-ai-entry__text">
                  <span className="patient-ai-entry__title">
                    {t("layout.aiConsultant")}
                  </span>
                </span>
              </button>
              <button
                type="button"
                className="button-default patient-history-entry"
                onClick={() => setShowPayments(true)}>
                <img
                  src={userMoney}
                  alt=""
                  className="patient-history-entry__icon"
                />
                {t("layout.finance")}
              </button>
              <button
                type="button"
                className="button-default patient-history-entry"
                onClick={() => setShowHistory(true)}>
                <img
                  src={userHistory}
                  alt=""
                  className="patient-history-entry__icon"
                />
                {t("layout.history")}
              </button>
            </div>
          </div>
          <EditableModal
            show={showBriefEdit}
            onHide={() => setShowBriefEdit(false)}
            title={t("layout.editTitle")}
            initialData={briefInitialData}
            fields={briefEditFields}
            onSave={handleSaveBrief}
          />
          <Modal
            show={showAiChat}
            onHide={() => setShowAiChat(false)}
            size="md"
            centered
            className="patient-ai-chat-modal">
            <Modal.Header closeButton>
              <Modal.Title>{t("layout.aiConsultant")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="patient-ai-chat-stub">
                {t("layout.aiStub")}
              </div>
              {/* {showAiChat ? <PatientAnalysesAI embedded /> : null} */}
            </Modal.Body>
          </Modal>
          <Modal
            show={showPayments}
            onHide={() => setShowPayments(false)}
            size="xl"
            centered
            className="patient-history-modal">
            <Modal.Header closeButton>
              <Modal.Title>{t("layout.finance")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>{showPayments ? <PatientPayments /> : null}</Modal.Body>
          </Modal>
          <Modal
            show={showHistory}
            onHide={() => setShowHistory(false)}
            size="xl"
            centered
            className="patient-history-modal">
            <Modal.Header closeButton>
              <Modal.Title>{t("layout.tabs.history")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {showHistory ? <PatientHistory embedded /> : null}
            </Modal.Body>
          </Modal>
        </Col>
      </Row>
    </Container>
  );
}
